import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Settings2, GripVertical, RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ColumnSettingsModal — настройка видимости и порядка колонок таблицы
 *
 * Аналог настройки колонок в Bitrix24:
 * - Шестерёнка в заголовке таблицы
 * - Модалка с чекбоксами по блокам
 * - Drag-and-drop для изменения порядка
 */

// Block names for grouping columns (Russian labels)
const BLOCK_LABELS = {
  main: 'Основные',
  passport: 'Паспорт проекта',
  assessment: 'Оценка',
  commitments: 'Обязательства',
  expenses: 'Затраты',
  subcontract: 'Субподряд и налоги',
  final_calc: 'Итоговая калькуляция',
};

/**
 * DraggableColumnList — список колонок с drag-and-drop
 */
const DraggableColumnList = ({
  columns,
  visibleKeys,
  onToggle,
  onReorder,
  roleFilter,
}) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const dragNodeRef = useRef(null);

  // Filter columns by role if provided
  const filteredColumns = roleFilter
    ? columns.filter(roleFilter)
    : columns;

  // Group columns by block
  const groupedColumns = {};
  filteredColumns.forEach(col => {
    const block = col.block || 'other';
    if (!groupedColumns[block]) {
      groupedColumns[block] = [];
    }
    groupedColumns[block].push(col);
  });

  const handleDragStart = (e, colKey, blockKey) => {
    setDraggedItem({ key: colKey, block: blockKey });
    // Find the row element (parent div with draggable)
    const row = e.target.closest('[draggable="true"]');
    dragNodeRef.current = row;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colKey);
    // Delay to show drag styling
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
    dragNodeRef.current = null;
  };

  const handleDragOver = (e, colKey) => {
    e.preventDefault();
    if (draggedItem && draggedItem.key !== colKey) {
      setDragOverItem(colKey);
    }
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    if (draggedItem && draggedItem.key !== targetKey) {
      // Pass keys directly - the hook handles the reordering
      onReorder(draggedItem.key, targetKey);
    }
    handleDragEnd();
  };

  return (
    <div className="space-y-4">
      {Object.entries(groupedColumns).map(([blockKey, blockColumns]) => (
        <div key={blockKey}>
          <div className="text-sm font-medium text-gray-500 mb-2 px-1">
            {BLOCK_LABELS[blockKey] || blockKey}
          </div>
          <div className="space-y-1">
            {blockColumns.map(col => (
              <div
                key={col.key}
                draggable="true"
                onDragStart={(e) => handleDragStart(e, col.key, blockKey)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, col.key)}
                onDragEnter={(e) => { e.preventDefault(); setDragOverItem(col.key); }}
                onDragLeave={() => setDragOverItem(null)}
                onDrop={(e) => handleDrop(e, col.key)}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded-md transition-all select-none",
                  "hover:bg-gray-100 cursor-grab active:cursor-grabbing",
                  dragOverItem === col.key && "bg-blue-100 border-2 border-blue-400 border-dashed",
                  draggedItem?.key === col.key && "opacity-50",
                  visibleKeys.includes(col.key)
                    ? "bg-blue-50/50"
                    : "bg-gray-50"
                )}
              >
                <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0 pointer-events-none" />
                <Checkbox
                  id={`col-${col.key}`}
                  checked={visibleKeys.includes(col.key)}
                  onCheckedChange={() => onToggle(col.key)}
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  htmlFor={`col-${col.key}`}
                  className="text-sm cursor-pointer flex-1 select-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(col.key);
                  }}
                >
                  {col.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * ColumnSettingsButton — кнопка-триггер для открытия модалки
 */
export const ColumnSettingsButton = ({ onClick, isCustomized }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={onClick}
    className={cn(
      "h-8 w-8 p-0",
      isCustomized && "text-blue-600"
    )}
    title="Настроить колонки"
  >
    <Settings2 className="w-4 h-4" />
  </Button>
);

/**
 * ColumnSettingsModal — основной компонент модалки
 */
const ColumnSettingsModal = ({
  open,
  onOpenChange,
  columns,
  visibleColumnKeys,
  columnOrder,
  onToggleColumn,
  onReorderColumns,
  onApply,
  onReset,
  saving = false,
  isCustomized = false,
  roleFilter = null, // (col) => boolean — filter columns by role
}) => {
  // Create ordered columns list based on columnOrder
  const orderedColumns = [...columns].sort((a, b) => {
    const aIndex = columnOrder.indexOf(a.key);
    const bIndex = columnOrder.indexOf(b.key);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  const handleSelectAll = () => {
    const allKeys = (roleFilter ? columns.filter(roleFilter) : columns).map(c => c.key);
    // If all are selected, deselect all; otherwise select all
    if (allKeys.every(k => visibleColumnKeys.includes(k))) {
      allKeys.forEach(k => {
        if (visibleColumnKeys.includes(k)) {
          onToggleColumn(k);
        }
      });
    } else {
      allKeys.forEach(k => {
        if (!visibleColumnKeys.includes(k)) {
          onToggleColumn(k);
        }
      });
    }
  };

  const handleDeselectAll = () => {
    const allKeys = (roleFilter ? columns.filter(roleFilter) : columns).map(c => c.key);
    allKeys.forEach(k => {
      if (visibleColumnKeys.includes(k)) {
        onToggleColumn(k);
      }
    });
  };

  const filteredCount = roleFilter
    ? columns.filter(roleFilter).length
    : columns.length;

  const visibleCount = roleFilter
    ? visibleColumnKeys.filter(k => columns.filter(roleFilter).some(c => c.key === k)).length
    : visibleColumnKeys.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Настройка колонок
          </DialogTitle>
          <DialogDescription>
            Выберите колонки для отображения и измените их порядок перетаскиванием
          </DialogDescription>
        </DialogHeader>

        {/* Selection controls */}
        <div className="flex items-center gap-2 px-1 py-2 border-b">
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={handleSelectAll}>
            Выбрать все
          </Button>
          <span className="text-gray-300">|</span>
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={handleDeselectAll}>
            Отменить все
          </Button>
          <span className="flex-1" />
          <span className="text-xs text-gray-500">
            {visibleCount} из {filteredCount}
          </span>
        </div>

        {/* Columns list with drag-and-drop */}
        <div className="overflow-y-auto max-h-[50vh] pr-2 -mr-2">
          <div className="pb-4">
            <DraggableColumnList
              columns={orderedColumns}
              visibleKeys={visibleColumnKeys}
              onToggle={onToggleColumn}
              onReorder={onReorderColumns}
              roleFilter={roleFilter}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {isCustomized && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              disabled={saving}
              className="text-gray-500"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Сбросить
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={onApply} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Сохранение...
              </>
            ) : (
              'Применить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ColumnSettingsModal;
